import { baseApi } from '@shared/store/api/baseApi';
export type WidgetAppearance = {
  mode: 'chat' | 'voice';
  size: 'full' | 'compact' | 'tiny';
  placement: 'bottom-right' | 'bottom-left';
  colors: Partial<
    Record<
      'background' | 'accent' | 'messageBackground' | 'accentText' | 'text' | 'secondaryText',
      string
    >
  >;
  title: string;
  cta: string;
  start: string;
  end: string;
  placeholder: string;
  avatar: string | null;
  termsEnabled: boolean;
  termsUrl: string | null;
};
export type WidgetConfiguration = {
  enabled: boolean;
  allowedOrigins: string[];
  budget: {
    creditsPerSession: string;
    creditsPerDay: string;
    maxConcurrentSessions: number;
    maxSessionSeconds: number;
  };
  appearance: WidgetAppearance;
};
export type WidgetRecord = {
  id: string;
  campaignId: string;
  agentId: string;
  revision: number;
  configuration: WidgetConfiguration;
  published: { versionId: string; configuration: WidgetConfiguration; publishedAt: string } | null;
  available: boolean;
};
export type PublicWidget = {
  id: string;
  available: boolean;
  appearance: WidgetAppearance | null;
  versionId: string | null;
  websocketUrl: string | null;
};
export type WidgetGrant = {
  sessionId: string;
  token: string;
  expiresAt: string;
  websocketUrl: string;
};
type Target = { scope: string; campaignId: string; agentId: string };
const url = (p: Target) => `/v2/workspace/widgets/${p.campaignId}/${p.agentId}`;
const api = baseApi.enhanceEndpoints({ addTagTypes: ['Widget'] }).injectEndpoints({
  endpoints: (build) => ({
    workspaceWidget: build.query<{ widget: WidgetRecord | null }, Target>({
      query: url,
      providesTags: ['Widget'],
    }),
    saveWorkspaceWidget: build.mutation<
      WidgetRecord,
      Target & { revision: number | null; configuration: WidgetConfiguration }
    >({
      query: ({ revision, configuration, ...target }) => ({
        url: url(target),
        method: 'PUT',
        body: { revision, configuration },
      }),
      invalidatesTags: ['Widget'],
    }),
    publishWorkspaceWidget: build.mutation<WidgetRecord, { id: string; revision: number }>({
      query: ({ id, revision }) => ({
        url: `/v2/workspace/widgets/${id}/publish`,
        method: 'POST',
        body: { revision },
      }),
      invalidatesTags: ['Widget'],
    }),
    disableWorkspaceWidget: build.mutation<WidgetRecord, { id: string; revision: number }>({
      query: ({ id, revision }) => ({
        url: `/v2/workspace/widgets/${id}/disable`,
        method: 'POST',
        body: { revision },
      }),
      invalidatesTags: ['Widget'],
    }),
  }),
});
export const {
  useWorkspaceWidgetQuery,
  useSaveWorkspaceWidgetMutation,
  usePublishWorkspaceWidgetMutation,
  useDisableWorkspaceWidgetMutation,
} = api;
